import i18n from "i18next";
import { useCallback, useEffect, useState } from "react";
import { isAbortError } from "../../api/http/httpClient";
import { reportUiError, toUiError, type UiError } from "../../errors/uiError";

/** The state of one pane's data. Each pane loads, shows and fails on its own. */
export type LoadState<T> =
  | { readonly status: "loading" }
  | { readonly status: "loaded"; readonly value: T }
  | { readonly status: "failed"; readonly error: UiError };

export interface LoadHandle<T> {
  readonly state: LoadState<T>;
  readonly reload: () => void;
  /** Load again, but keep showing the current value until the new one arrives. */
  readonly refresh: () => void;
}

/**
 * Run `load` when the pane mounts, whenever `load` changes, and on `reload`. Wrap `load` in
 * `useCallback` with the values it reads, so it changes exactly when they do. The previous
 * request is aborted, and its result ignored, when a new one starts or the pane unmounts.
 */
export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>): LoadHandle<T> {
  const [request, setRequest] = useState({ load, sequence: 0 });
  const [state, setState] = useState<LoadState<T>>({ status: "loading" });

  // A new `load` starts a new request; render-time state adjustment, not an effect.
  if (request.load !== load) {
    setRequest({ load, sequence: request.sequence + 1 });
    setState({ status: "loading" });
  }

  useEffect(() => {
    const controller = new AbortController();
    request.load(controller.signal).then(
      (value) => {
        if (!controller.signal.aborted) {
          setState({ status: "loaded", value });
        }
      },
      (caught: unknown) => {
        if (controller.signal.aborted || isAbortError(caught)) {
          return;
        }
        const error = toUiError(caught);
        reportUiError(error);
        setState({ status: "failed", error });
      },
    );
    return () => {
      controller.abort();
    };
  }, [request]);

  const reload = useCallback(() => {
    setRequest((current) => ({ load: current.load, sequence: current.sequence + 1 }));
    setState({ status: "loading" });
  }, []);

  const refresh = useCallback(() => {
    setRequest((current) => ({ load: current.load, sequence: current.sequence + 1 }));
  }, []);

  // Requests carry the person's language (`httpClient`): a new language reads the data again,
  // showing the current value until the new one arrives.
  useEffect(() => {
    i18n.on("languageChanged", refresh);
    return () => {
      i18n.off("languageChanged", refresh);
    };
  }, [refresh]);

  return { state, reload, refresh };
}
